/**
 * Panneau « Créature » de l'éditeur d'une SKILLCARD : la créature origami SVG
 * du verso (migration 0274).
 *
 * - Générer / Régénérer par l'IA (consigne facultative : « plus d'ailes »…).
 * - Exporter le SVG (calques base / évolution, nuances conservées) pour le
 *   retoucher dans Inkscape, Figma…, puis le réimporter : aperçu d'abord,
 *   enregistrement ensuite.
 * - Supprimer : retour à la créature par défaut.
 *
 * L'aperçu de la carte (route parente) affiche la créature importée tant
 * qu'elle n'est pas enregistrée, via `onPreview`.
 */

import { useRef, useState, type ChangeEvent } from 'react';
import { Download, Sparkles, Trash2, Upload } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  DEFAULT_CARD_CREATURE,
  creatureToSvg,
  parseCardCreature,
  type CardCreature,
} from '@jeraisonne/db/cards';
import { useGenerateCreature, useSaveCreature, type CardRow } from '@/lib/cards/api';
import { CREATURE_SVG_MAX_BYTES, svgToCreature } from '@/lib/cards/creature-svg';
import type { PipelineModelId } from '@/lib/admin/pipeline-models';
import { AiModelSelect, CARD_DEFAULT_MODEL } from './ai-model-select';

export interface CardCreaturePanelProps {
  card: Pick<CardRow, 'id' | 'short_code' | 'creature'>;
  /** Teinte courante de la carte (export, et nuances retrouvées à l'import). */
  accent: string;
  /** Créature importée non enregistrée (aperçu), ou `null` quand il n'y en a plus. */
  onPreview: (creature: CardCreature | null) => void;
  /** Message affiché par la page (survit au remontage de l'éditeur après enregistrement). */
  onMessage: (message: { kind: 'ok' | 'error'; text: string } | null) => void;
}

export function CardCreaturePanel({
  card,
  accent,
  onPreview,
  onMessage: setMessage,
}: CardCreaturePanelProps) {
  const generate = useGenerateCreature();
  const save = useSaveCreature();
  const fileRef = useRef<HTMLInputElement>(null);
  const [instruction, setInstruction] = useState('');
  const [model, setModel] = useState<PipelineModelId>(CARD_DEFAULT_MODEL);
  const [pending, setPending] = useState<CardCreature | null>(null);
  const current = parseCardCreature(card.creature);
  const busy = generate.isPending || save.isPending;

  const setPreview = (c: CardCreature | null) => {
    setPending(c);
    onPreview(c);
  };

  const onGenerate = async () => {
    if (current && !window.confirm('Remplacer la créature actuelle par une nouvelle ?')) return;
    setMessage(null);
    setPreview(null);
    try {
      await generate.mutateAsync({
        card_id: card.id,
        instruction: instruction.trim() || undefined,
        model,
      });
      setMessage({
        kind: 'ok',
        text: 'Créature dessinée. Republie la carte pour la mettre en ligne.',
      });
    } catch (e) {
      setMessage({
        kind: 'error',
        text: e instanceof Error ? e.message : 'Génération impossible.',
      });
    }
  };

  const onExport = () => {
    const svg = creatureToSvg(pending ?? current ?? DEFAULT_CARD_CREATURE, accent);
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `skillcard-${card.short_code}-creature.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMessage(null);
    if (file.size > CREATURE_SVG_MAX_BYTES) {
      setMessage({ kind: 'error', text: 'Fichier trop lourd (200 ko maximum).' });
      return;
    }
    const result = svgToCreature(await file.text(), accent);
    if (!result.ok) {
      setMessage({ kind: 'error', text: result.error });
      return;
    }
    setPreview(result.creature);
    setMessage({
      kind: 'ok',
      text: 'Aperçu de la créature importée : enregistre-la pour la garder.',
    });
  };

  const onSaveImport = async () => {
    if (!pending) return;
    try {
      await save.mutateAsync({ id: card.id, creature: pending });
      setPreview(null);
      setMessage({
        kind: 'ok',
        text: 'Créature enregistrée. Republie la carte pour la mettre en ligne.',
      });
    } catch (e) {
      setMessage({
        kind: 'error',
        text: e instanceof Error ? e.message : 'Enregistrement impossible.',
      });
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Supprimer la créature ? La carte affichera la créature par défaut.'))
      return;
    try {
      await save.mutateAsync({ id: card.id, creature: null });
      setPreview(null);
      setMessage({ kind: 'ok', text: 'Créature supprimée.' });
    } catch (e) {
      setMessage({
        kind: 'error',
        text: e instanceof Error ? e.message : 'Suppression impossible.',
      });
    }
  };

  return (
    <section
      aria-labelledby="card-creature-title"
      className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4"
    >
      <div>
        <h2 id="card-creature-title" className="text-ink-900 font-display text-base font-bold">
          Créature origami
        </h2>
        <p className="text-ink-500 text-xs">
          {current
            ? `Créature actuelle : ${current.species}. Elle prend la couleur de la carte.`
            : 'Pas encore de créature : la carte affiche le renard par défaut.'}
        </p>
      </div>

      <label className="block space-y-1">
        <span className="text-ink-600 text-xs font-semibold">Souhait pour l’IA (optionnel)</span>
        <input
          className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 py-2 text-sm"
          maxLength={300}
          placeholder="Ailes plus grandes, posture en vol…"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
        />
      </label>

      <AiModelSelect value={model} onChange={setModel} disabled={busy} />

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Button
          fullWidth
          className="sm:w-auto"
          loading={generate.isPending}
          disabled={busy}
          onClick={() => void onGenerate()}
        >
          <span className="flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4" aria-hidden />
            {current ? 'Régénérer la créature' : 'Générer la créature'}
          </span>
        </Button>
        <Button
          variant="outline"
          fullWidth
          className="sm:w-auto"
          onClick={onExport}
          disabled={busy}
        >
          <span className="flex items-center justify-center gap-2">
            <Download className="h-4 w-4" aria-hidden /> Exporter le SVG
          </span>
        </Button>
        <Button
          variant="outline"
          fullWidth
          className="sm:w-auto"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          <span className="flex items-center justify-center gap-2">
            <Upload className="h-4 w-4" aria-hidden /> Importer un SVG
          </span>
        </Button>
        {current && !pending && (
          <Button
            variant="ghost"
            fullWidth
            className="sm:w-auto"
            onClick={() => void onDelete()}
            disabled={busy}
          >
            <span className="flex items-center justify-center gap-2">
              <Trash2 className="h-4 w-4" aria-hidden /> Supprimer
            </span>
          </Button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".svg,image/svg+xml"
          className="sr-only"
          aria-label="Fichier SVG de la créature"
          tabIndex={-1}
          onChange={(e) => void onFile(e)}
        />
      </div>

      {pending && (
        <div className="bg-primary-50 flex flex-col gap-2 rounded-xl p-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-ink-800 text-sm">
            Créature importée ({pending.base.length} plis) — visible dans l’aperçu.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              size="sm"
              fullWidth
              className="sm:w-auto"
              loading={save.isPending}
              onClick={() => void onSaveImport()}
            >
              Enregistrer
            </Button>
            <Button
              size="sm"
              variant="ghost"
              fullWidth
              className="sm:w-auto"
              onClick={() => {
                setPreview(null);
                setMessage(null);
              }}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
